import { isBookingNotRead } from '../../lib/bookingConfigRead';
import type { LocaleContextValue } from '../../i18n';

// ─────────────────────────────────────────────────────────────────────
// THE PRICE EDITOR'S SAVE, ONE DOCUMENT AT A TIME (#1370, owner decision
// 2026-11-04).
//
// «Lagre alle endringer» writes up to three documents: the venue setup's
// default price (`booking_config_v1`), the zones (`booking_zones_v1`) and the
// guest default price (`booking_guest_prices_v1`). There is NO transaction
// across them, so the save cannot be atomic and must not pretend to be. What
// it can be is explicit: it stops at the first write that fails and sends
// nothing after it, and the message says exactly what is KNOWN about each part:
//
//   saved          the write answered 2xx;
//   not saved      the failed write is PROVEN not applied — a version conflict
//                  (409), or refused before it was sent;
//   unknown        the failed write MAY have landed — a 5xx, a dropped
//                  connection, any other error. Never called «not saved»:
//                  `Storage` itself treats these as ambiguous, and the re-read
//                  is how staff find out;
//   not attempted  every part after the failed one.
// ─────────────────────────────────────────────────────────────────────

/** A write's answer, as the booking-settings slice gives it. */
export interface PriceWriteResult {
  success: boolean;
  error?: unknown;
  code?: unknown;
  /** `conflict` | `refused` | `unknown` — see `BookingSettingsUpdateResult`. */
  outcome?: unknown;
}

/** One part of the save: what staff call it, and the write that saves it. */
export interface PriceSaveStep {
  label: string;
  write: () => Promise<PriceWriteResult>;
}

/**
 * What is known about the failed write. `notRead` and `conflict` and `refused`
 * are proven not applied; `unknown` is everything else, conservatively.
 */
export type PriceFailedState = 'notRead' | 'conflict' | 'refused' | 'unknown';

/** Where a save stopped. */
export interface PriceSaveFailure {
  /** The parts whose writes answered 2xx, in order. */
  saved: string[];
  /** The part whose write failed. */
  failed: string;
  /** What is known about that write. */
  failedState: PriceFailedState;
  /** The parts after it — never sent. */
  notAttempted: string[];
  result: PriceWriteResult;
}

/** Proven not applied only when the slice says so; anything else may have landed. */
export function failedStateOf(result: PriceWriteResult): PriceFailedState {
  if (isBookingNotRead(result.code)) return 'notRead';
  if (result.outcome === 'conflict') return 'conflict';
  if (result.outcome === 'refused') return 'refused';
  return 'unknown';
}

/**
 * Run the writes in order; stop at the first that fails. Answers where it
 * stopped, or `null` when every part was saved.
 */
export async function runPriceSave(steps: PriceSaveStep[]): Promise<PriceSaveFailure | null> {
  const saved: string[] = [];
  for (const [i, step] of steps.entries()) {
    const result = await step.write();
    if (!result.success) {
      return {
        saved,
        failed: step.label,
        failedState: failedStateOf(result),
        notAttempted: steps.slice(i + 1).map((s) => s.label),
        result,
      };
    }
    saved.push(step.label);
  }
  return null;
}

/**
 * What staff are told when the save stopped, one line each: what was saved
 * (when anything was), what is known about the failed part, what was not
 * attempted (when anything was not), and the way out.
 */
export function priceSaveFailureMessage(
  stopped: PriceSaveFailure,
  t: LocaleContextValue['t'],
): string {
  const { failedState, result } = stopped;
  const lines: string[] = [];
  if (stopped.saved.length) {
    lines.push(t('booking.view.priceSave.saved', { parts: stopped.saved.join(', ') }));
  }
  if (failedState === 'notRead') {
    // Refused before anything was sent (an unread document, #1359/#1362): the
    // approved sentence, which already says to press «Last inn på nytt». Alone
    // when nothing else happened, as before.
    if (!stopped.saved.length && !stopped.notAttempted.length) {
      return t('booking.view.configUnread.saveRefused');
    }
    lines.push(t('booking.view.configUnread.saveRefused'));
  } else if (failedState === 'conflict') {
    lines.push(t('booking.view.priceSave.conflict', { part: stopped.failed }));
  } else if (failedState === 'refused') {
    lines.push(
      t('booking.view.priceSave.refused', { part: stopped.failed, error: String(result.error) }),
    );
  } else {
    lines.push(
      t('booking.view.priceSave.unknown', { part: stopped.failed, error: String(result.error) }),
    );
  }
  if (stopped.notAttempted.length) {
    lines.push(
      t('booking.view.priceSave.notAttempted', { parts: stopped.notAttempted.join(', ') }),
    );
  }
  // The way out: the re-read, which either way the failed document needs
  // (`Storage` holds it must-refresh). The unknown line says it already.
  if (failedState === 'conflict' || failedState === 'refused') {
    lines.push(t('booking.view.priceSave.reload'));
  }
  return lines.join('\n');
}
